import { useInfiniteQuery } from '@tanstack/react-query';
import { isEmpty, isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';
import { useIsMobileDevice, usePrevious } from '@hivebrite/react-hooks';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { displayXhrError } from 'hb-react/frontoffice/utils/globalUtils';
import { UjErrorBoundary } from 'hb-react/shared/components/ErrorBoundary';

import ActionBar from './components/ActionBar';
import { CommentForm } from './components/CommentForm';
import { CommentWithReplies } from './components/CommentWithReplies';
import { LoadMore } from './components/LoadMore';
import { useCommentsClient } from './hooks/commentsClient';
import {
  addComment,
  deleteComment,
  editComment,
  orderCommentsByDesc,
} from './utils';

const Comments = ({
  autoFocus = false,
  commentableId,
  commentableType,
  comments: initialComments,
  options,
  isOpenInModal = false,
  onBlur,
  onCommentsUpdate,
  onNewCommentCallback,
  totalComments: initialTotalComments,
}) => {
  const { currentUser } = useDefaultAppContexts();
  const isMobileDevice = useIsMobileDevice();

  const canDisplayEmojiPicker = !isMobileDevice;

  const [comments, setComments] = useState([]);
  const [totalComments, setTotalComments] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [displayedComments, setDisplayedComments] = useState([]);
  const [sortBy, setSortBy] = useState('recents');

  const quillRef = useRef();

  const prevInitialComments = usePrevious(initialComments);

  const { getComments } = useCommentsClient();

  const infiniteQueryKey = useMemo(
    () => [
      'paginated-comments',
      commentableId,
      commentableType,
      sortBy,
      isOpenInModal ? 'modal' : 'page',
    ],
    [commentableId, commentableType, sortBy, isOpenInModal],
  );

  const { isFetching, hasNextPage, data, fetchNextPage } = useInfiniteQuery({
    queryKey: infiniteQueryKey,
    queryFn: async ({ pageParam }) => {
      try {
        return await getComments({
          commentableId,
          commentableType,
          page: pageParam,
          sortBy,
          perPage: 5,
        });
      } catch (error) {
        if (error?.response?.status === 403) {
          displayXhrError(error);
        }
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: ({ _metadata: { page, has_next_page: hasNext } }) =>
      hasNext ? page + 1 : undefined,
    enabled: infiniteQueryKey.includes(sortBy),
  });

  const isLoading = isFetching && currentPage === 0;

  const focusCommentInput = () => {
    setTimeout(() => quillRef?.current?.focus(), 50);
  };

  const prepareDisplayedComments = useCallback(
    (newComments, newTotalComments) => {
      if (newComments.length > 0) {
        setDisplayedComments(orderCommentsByDesc(newComments, sortBy));
      } else {
        setDisplayedComments(newComments);
      }

      if (!isEqual(newComments, initialComments)) {
        onCommentsUpdate?.({
          comments: newComments,
          totalComments: newTotalComments,
        });
      }
    },
    [onCommentsUpdate, initialComments, sortBy],
  );

  const handleUpdateComment = useCallback(
    ({ comment }) => {
      const updatedComments = editComment({ comment, comments });

      setComments(updatedComments);

      prepareDisplayedComments(updatedComments, totalComments);
    },
    [comments, totalComments, setComments, prepareDisplayedComments],
  );

  const handleDeleteComment = useCallback(
    ({ id }) => {
      const updatedComments = deleteComment({ id, comments });

      setComments(updatedComments);
      setTotalComments(totalComments - 1);

      prepareDisplayedComments(updatedComments, totalComments - 1);
    },
    [
      comments,
      totalComments,
      setComments,
      setTotalComments,
      prepareDisplayedComments,
    ],
  );

  const handleNewComment = useCallback(
    ({ newComment }) => {
      const updatedComments = addComment({ comment: newComment, comments });

      setComments(updatedComments);
      setTotalComments(totalComments + 1);

      onNewCommentCallback?.();

      prepareDisplayedComments(updatedComments, totalComments + 1);
    },
    [
      comments,
      totalComments,
      setComments,
      setTotalComments,
      prepareDisplayedComments,
      onNewCommentCallback,
    ],
  );

  const handleToggleLike = useCallback(
    ({ newLikes, commentId }) => {
      const index = comments.findIndex((element) => element.id === commentId);

      if (index >= 0) {
        const newComments = [...comments];
        const element = newComments[index];

        const likedComment = {
          ...element,
          likes: newLikes,
        };

        newComments[index] = likedComment;

        setComments(newComments);

        prepareDisplayedComments(newComments, totalComments);
      }
    },
    [comments, totalComments, setComments, prepareDisplayedComments],
  );

  const handleVote = useCallback(
    ({ commentId, state, score }) => {
      const index = comments.findIndex((element) => element.id === commentId);

      if (index >= 0) {
        const newComments = [...comments];
        const element = newComments[index];

        const votedComment = {
          ...element,
          votes: { state, score },
        };

        newComments[index] = votedComment;

        setComments(newComments);

        prepareDisplayedComments(newComments, totalComments);
      }
    },
    [comments, setComments, totalComments, prepareDisplayedComments],
  );

  const loadMoreComments = useCallback(() => {
    if (fetchNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage]);

  const handleChangeSortBy = useCallback(
    (sortByParam) => {
      setCurrentPage(0);
      setComments([]);
      setTotalComments(0);

      setSortBy(sortByParam);
    },
    [setSortBy],
  );

  useEffect(() => {
    if (
      !isEqual(prevInitialComments, initialComments) &&
      initialComments &&
      initialComments.length > 0
    ) {
      setComments(initialComments);
      setTotalComments(initialTotalComments);

      prepareDisplayedComments(initialComments, initialTotalComments);
    }
  }, [
    prevInitialComments,
    initialComments,
    initialTotalComments,
    prepareDisplayedComments,
  ]);

  useEffect(() => {
    if (!isFetching && !isEmpty(data)) {
      const { pages = [] } = data;

      const loadedCurrentPage = pages.length;

      if (loadedCurrentPage !== currentPage && pages[currentPage]) {
        const { comments: loadedComments, _metadata } = pages[currentPage];

        if (loadedComments.length > 0) {
          const newItems = loadedComments.filter(
            (item) =>
              comments.find((itemOriginal) => itemOriginal.id === item.id) ===
              undefined,
          );

          const newComments = [...comments, ...newItems];

          setComments(newComments);
          setTotalComments(_metadata.total_items);
          setCurrentPage(currentPage + 1);

          prepareDisplayedComments(newComments, _metadata.total_items);
        }
      }
    }
  }, [isFetching, data, comments, currentPage]);

  useEffect(() => {
    if (autoFocus) {
      focusCommentInput();
    }
  }, [autoFocus]);

  return (
    <UjErrorBoundary
      context={{
        tags: {
          module: 'FO|CommentsV2',
        },
      }}
    >
      {options?.votes?.enableVotes && (
        <ActionBar isLoading={isLoading} onChangeSortBy={handleChangeSortBy} />
      )}
      {isLoading ? (
        <Box py={6}>
          <Loader size={30} />
        </Box>
      ) : (
        <Box flexDirection="column" mt={5} pb={4}>
          <CommentForm
            autoFocus={autoFocus}
            canDisplayEmojiPicker={canDisplayEmojiPicker}
            commentableId={commentableId}
            commentableType={commentableType}
            handleOnFocus={focusCommentInput}
            onBlur={onBlur}
            onComment={handleNewComment}
            onUpdateComment={handleUpdateComment}
            ref={quillRef}
            user={currentUser}
          />
          {displayedComments.map((comment) => (
            <Box key={comment.id} data-testid="main-comment">
              <CommentWithReplies
                canDisplayEmojiPicker={canDisplayEmojiPicker}
                comment={comment}
                currentUser={currentUser}
                options={options}
                isOpenInModal={isOpenInModal}
                onDeleteComment={handleDeleteComment}
                onToggleLike={handleToggleLike}
                onUpdateComment={handleUpdateComment}
                onVote={handleVote}
              />
            </Box>
          ))}

          <Box alignSelf="flex-end">
            {hasNextPage && (
              <LoadMore
                isFetching={isFetching}
                loadMore={loadMoreComments}
                loadingText={s__('Comments|Loading...')}
                linkText={s__('Comments|See more comments')}
              />
            )}
          </Box>
        </Box>
      )}
    </UjErrorBoundary>
  );
};

export { Comments };
